<script>
    import {page, router} from "@inertiajs/svelte";
    import {Search, Sparkles, X} from "@lucide/svelte";
    import {Input} from "~/components/ui/input";
    import {Button} from "~/components/ui/button";
    import * as Tooltip from "~/components/ui/tooltip";
    import {searchQuery} from "~/components/searchStore";

    export let filters = {};

    // Keep the box text in sync when a search URL drives the page (direct link,
    // back button, a fresh /posts?q=… search). On pages without a q (detail
    // pages) we leave the store untouched so the text persists across nav.
    $: if (!isTagsWithSelection && $page.props.filters?.q) {
        $searchQuery = $page.props.filters.q;
    }

    let showTextSearchTip = typeof localStorage !== 'undefined'
        && (Date.now() - Number(localStorage.getItem('nyanbooru-text-search-tip-dismissed') || 0)) > 86400000;

    function dismissTip() {
        showTextSearchTip = false;
        localStorage.setItem('nyanbooru-text-search-tip-dismissed', Date.now());
    }

    let model;
    // Boundary-aware — a bare startsWith("/p") would also match /privacy.
    $: model = /^\/p([/?]|$)/.test($page.url) ? '/p' : '/m';

    $: isTagsWithSelection = $page.url.startsWith("/tags") && $page.props.selected;

    let contentQuery = "";
    $: if (isTagsWithSelection) {
        contentQuery = $page.props.q || "";
    }

    $: searchPlaceholder = isTagsWithSelection
        ? 'search in selected tags...'
        : ($page.component.startsWith('media_assets/') ? 'search images...' : 'search posts...');

    $: showEnhancedToggle = !isTagsWithSelection;

    function toggleEnhanced() {
        if (filters.enhanced) {
            delete filters.enhanced;
        } else {
            filters.enhanced = true;
        }
        filters = filters;

        // Dismiss the text search tip
        localStorage.setItem('nyanbooru-text-search-tip-dismissed', Date.now());

        const url = new URL($page.url, window.location.origin);
        router.get(url.pathname, filters);
    }

    function newSearch() {
        if (isTagsWithSelection) {
            let params = {};
            if (contentQuery) params.q = contentQuery;
            router.get(`/tags/${encodeURIComponent($page.props.selected)}`, params);
        } else {
            const params = { ...filters };
            if ($searchQuery.trim()) {
                params.q = $searchQuery;
            } else {
                delete params.q;
            }
            router.get(model, params);
        }
    }
</script>

<div class="flex items-center grow py-1 gap-1">
    <form onsubmit={(e) => { e.preventDefault(); newSearch(); }} class="flex items-center relative flex-1">
        <div class="flex items-center w-full relative">
            {#if isTagsWithSelection}
                <Input bind:value={contentQuery}
                       placeholder={searchPlaceholder}
                       type="text"
                       class="pr-10" />
            {:else}
                <Input bind:value={$searchQuery}
                       placeholder={searchPlaceholder}
                       type="text"
                       class="pr-10" />
            {/if}
            <button class="absolute right-3 text-muted-foreground hover:text-foreground transition-colors"
                    type="button"
                    onclick={newSearch}>
                <Search size={16} />
            </button>
        </div>
    </form>
    {#if showEnhancedToggle}
        <div class="relative">
            <Tooltip.Root>
                <Tooltip.Trigger>
                    {#snippet child({ props })}
                        <button {...props}
                                type="button"
                                class="p-2 rounded-md transition-colors {filters.enhanced
                                    ? 'text-primary bg-primary/10'
                                    : 'text-muted-foreground hover:text-foreground'}"
                                onclick={toggleEnhanced}>
                            <Sparkles size={16} />
                        </button>
                    {/snippet}
                </Tooltip.Trigger>
                <Tooltip.Content>
                    <p>強化文字搜尋 Enhanced text search (beta)</p>
                </Tooltip.Content>
            </Tooltip.Root>
            {#if showTextSearchTip && ($page.component.startsWith('posts/') || $page.component.startsWith('media_assets/'))}
                <!-- max-w keeps the 16rem bubble on screen at 280px-wide
                     devices; anchored right, it just gets narrower. Overflow
                     past the left edge cannot be scrolled to. The 4rem budget
                     covers the row's own px-4 plus the close button that sits
                     to the right of this anchor while the mobile search
                     overlay is open. -->
                <div class="absolute top-full right-0 mt-2 z-50 w-64 max-w-[calc(100vw-4rem)]">
                    <div class="absolute -top-1.5 right-3 w-3 h-3 rotate-45 bg-popover border-l border-t border-border"></div>
                    <div class="relative flex items-start gap-2 px-3 py-2.5 rounded-lg bg-popover border border-border shadow-lg text-sm">
                        <Sparkles size={14} class="flex-shrink-0 mt-0.5 text-primary" />
                        <p class="text-popover-foreground">
                            找不到想要的圖片嗎？試試<button class="text-primary font-medium hover:underline" onclick={() => { dismissTip(); toggleEnhanced(); }}>強化文字搜尋</button>。<br/>Can't find what you want? Try <button class="text-primary font-medium hover:underline" onclick={() => { dismissTip(); toggleEnhanced(); }}>enhanced text search</button>.
                        </p>
                        <button class="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                                onclick={dismissTip}>
                            <X size={12} />
                        </button>
                    </div>
                </div>
            {/if}
        </div>
    {/if}
</div>
